import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Pause, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X, Presentation, Volume2, VolumeX, MousePointer2, Sparkles, Minimize2, Maximize2, UtensilsCrossed } from 'lucide-react';
import { TEAM_AVATARS } from '../../components/teamAvatars';
import { CinematicIntro, PresentDirector, VoicePicker, FinaleScreen, VOICES, startTheme, stopTheme, pauseThemeSoft, resumeThemeSoft, ensureTheme, stopAllVoices, getMasterVolume, setMasterVolume, prewarmPresentation, prewarmLine, speakLine, getGuestName } from './PresentDirector';
import { CUISINE_PROFILES } from '../../lib/cuisines';
import { LangSwitch } from '../../components/LangSwitch';

/* Presentation Mode — full-screen auto-rotation of the best demo views for sales meetings. */

const VIEWS = [
  ['', 'Hoy · Daily Brief', 'Today · Daily Brief'],
  ['intelligence', 'Intelligence', 'Intelligence'],
  ['revenue', 'Revenue', 'Revenue'],
  ['menu', 'Menu Intelligence', 'Menu Intelligence'],
  ['reviews', 'Reviews', 'Reviews'],
  ['ai-executive', 'AI Executive', 'AI Executive'],
];
const INTERVAL = 90000; // safety ceiling — the director advances views when its script (incl. voice) finishes

export const PROFILE_CHOICES = CUISINE_PROFILES;
// The whole demo becomes their cuisine: new tenant cookie + fresh /restaurant.
export async function switchDemoProfile(profile, reopen = false) {
  try {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rest/demo/enter`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile }) });
    if (!r.ok) return false;
    if (reopen) {
      try {
        sessionStorage.setItem('kx_present_force', '1');
        if (reopen === 'resume') sessionStorage.setItem('kx_present_switch', '1'); // continue at the same scene
      } catch { /* noop */ }
    }
    window.location.href = '/restaurant';
    return true;
  } catch { return false; }
}

export function PresentButton({ isEs, onStart }) {
  return (
    <span className="inline-flex relative items-center">
      <span className="absolute inset-0 rounded-full pointer-events-none" data-testid="rest-present-beacon"
        style={{ animation: 'kx-present-beacon 2.6s ease-out infinite', border: '1.5px solid var(--rest-accent)' }} />
      <button onClick={onStart} data-testid="rest-present-btn"
        title={isEs ? 'Vive la experiencia — Rachel presenta tu restaurante' : 'Live the experience — Rachel presents your restaurant'}
        className="inline-flex items-center gap-1.5 rounded-full px-3 sm:px-3.5 py-1.5 text-[11.5px] font-bold text-white bg-[var(--rest-accent)] hover:brightness-110 hover:scale-105 transition-[transform,filter] duration-200"
        style={{ animation: 'kx-present-wiggle 5s ease-in-out infinite, kx-present-shadow 2.6s ease-in-out infinite' }}>
        <Presentation className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{isEs ? 'Vive la experiencia' : 'Live the experience'}</span>
        <span className="sm:hidden">{isEs ? 'Experiencia' : 'Experience'}</span>
      </button>
      <MousePointer2 className="hidden sm:block w-4 h-4 text-[var(--rest-accent)] absolute pointer-events-none" data-testid="rest-present-pointer"
        style={{ right: -10, bottom: -12, animation: 'kx-pointer-nudge 6s ease-in-out infinite', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.35))' }} />
      <style>{`
        @keyframes kx-present-beacon { 0% { transform: scale(1); opacity: 0.7; } 70% { transform: scale(1.35); opacity: 0; } 100% { opacity: 0; } }
        @keyframes kx-present-wiggle { 0%, 86%, 100% { transform: rotate(0); } 90% { transform: rotate(-2.5deg) scale(1.05); } 94% { transform: rotate(2deg) scale(1.05); } 97% { transform: rotate(-1deg); } }
        @keyframes kx-present-shadow {
          0%, 100% { box-shadow: 0 0 0 0 var(--rest-accent); }
          50% { box-shadow: 0 0 16px 1px var(--rest-accent); }
        }
        @keyframes kx-pointer-nudge {
          0%, 70%, 100% { transform: translate(0, 0); opacity: 0.85; }
          80% { transform: translate(-7px, -8px); opacity: 1; }
          86% { transform: translate(-7px, -8px) scale(0.85); }
          92% { transform: translate(0, 0); opacity: 0.85; }
        }
      `}</style>
    </span>
  );
}

function BeginGate({ isEs, onBegin, onAutoBegin, resumeInfo, onResume, dedication, dedicationSign, demoSlug, guestName }) {
  const [guide, setGuide] = useState(false);
  const [pos, setPos] = useState({ x: -80, y: 0 });
  const [autoLeft, setAutoLeft] = useState(20); // no click? the show starts itself
  const btnRef = React.useRef(null);
  // Browser prefers the other language? Offer it — never leave the Spanish-speaking world out.
  const otherLangHint = (() => {
    try { return (navigator.language || '').toLowerCase().startsWith('es') !== isEs; } catch { return false; }
  })();

  // If nothing happens in 20s, the experience begins on its own — and any stray touch
  // simply restarts the clock. No one is ever left stranded on this screen.
  // Dedications are the exception: the gift starts only on her tap, so the voice is
  // guaranteed from the very first second — never a silent, out-of-order love letter.
  useEffect(() => {
    if (dedication) return undefined;
    let iv;
    const startCount = () => {
      let left = 20;
      setAutoLeft(20);
      clearInterval(iv);
      iv = setInterval(() => {
        left -= 1;
        setAutoLeft(left);
        if (left <= 0) { clearInterval(iv); if (onAutoBegin) onAutoBegin(); }
      }, 1000);
    };
    startCount();
    const cancel = (e) => { if (e.isTrusted) startCount(); };
    window.addEventListener('pointerdown', cancel, true);
    return () => { clearInterval(iv); window.removeEventListener('pointerdown', cancel, true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onAutoBegin, dedication]);

  // Warm Rachel's first words while the guest reads — the voice arrives right on cue.
  useEffect(() => {
    let v = 'shimmer';
    try { v = localStorage.getItem('kx_rachel_voice') || 'shimmer'; } catch { /* noop */ }
    prewarmPresentation(isEs ? 'es' : 'en', v, demoSlug || '');
  }, [isEs, demoSlug]);

  // If their cursor doesn't move, Rachel's hand glides to the button: "click here — it all starts here".
  useEffect(() => {
    let t; let moved = 0; let lx = null; let ly = null;
    const start = () => {
      const b = btnRef.current;
      if (!b) return;
      const r = b.getBoundingClientRect();
      setPos({ x: Math.max(30, r.left - 150), y: Math.min(window.innerHeight - 60, r.bottom + 90) });
      setGuide(true);
      setTimeout(() => setPos({ x: r.left + r.width * 0.62, y: r.top + r.height * 0.6 }), 250);
    };
    const arm = () => { clearTimeout(t); t = setTimeout(start, 6000); };
    arm();
    const onMove = (e) => {
      if (!e.isTrusted) return;
      if (lx !== null) moved += Math.abs(e.clientX - lx) + Math.abs(e.clientY - ly);
      lx = e.clientX; ly = e.clientY;
      if (moved > 60) { moved = 0; setGuide(false); arm(); }
    };
    const onDown = (e) => { if (e.isTrusted) { setGuide(false); clearTimeout(t); } };
    window.addEventListener('mousemove', onMove, true);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center px-6" data-testid="present-begin-gate"
      style={{ background: 'radial-gradient(ellipse at center, #16171a 0%, #060607 78%)' }}>
      <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: 'radial-gradient(ellipse 60% 38% at 50% 58%, rgba(247,127,17,0.09), transparent 70%)' }} />
      {!dedication && (
        <div className="absolute top-5 right-5 text-right" data-testid="present-lang-switch">
          <LangSwitch dark />
          {otherLangHint && (
            <p className="mt-1.5 text-[10.5px] text-[#F77F11]/90 font-semibold" data-testid="present-lang-hint"
              style={{ animation: 'kx-begin-glow 2.6s ease-in-out infinite' }}>
              {isEs ? 'Prefer English? →' : '¿Prefieres español? →'}
            </p>
          )}
        </div>
      )}
      <div className="max-w-3xl text-center" style={{ animation: 'kx-intro-line 1.3s cubic-bezier(0.16,1,0.3,1) both' }}>
        {dedication ? (
          <p className="text-[11px] uppercase tracking-[0.4em] font-bold text-[#F77F11]" data-testid="present-dedication-ribbon"
            style={{ animation: 'kx-begin-glow 2.6s ease-in-out infinite' }}>
            {dedicationSign
              ? `❤ PARA ${dedication.toUpperCase()} · DE ${dedicationSign.toUpperCase()}`
              : `❤ PARA ${dedication.toUpperCase()} · ALGUIEN QUE TE AMA PREPARÓ ESTO`}
          </p>
        ) : (
          <p className="text-[10px] uppercase tracking-[0.5em] font-bold text-[#F77F11]">KITXENS · RESTAURANT AI OS</p>
        )}
        <h2 className="mt-5 text-3xl sm:text-5xl text-white leading-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
          {isEs ? 'Descubre cómo KITXENS llevaría tu restaurante.' : 'Experience how KITXENS would run your restaurant.'}
        </h2>
        <span className="relative inline-block mt-10">
          {guide && (
            <span className="pointer-events-none absolute -inset-2 rounded-full border-2 border-[#F77F11]"
              style={{ animation: 'kx-gate-halo 1.6s ease-in-out infinite' }} />
          )}
          <button ref={btnRef} onClick={onBegin} data-testid="present-begin-btn"
            className="inline-flex items-center gap-2.5 rounded-full px-8 py-4 text-[15px] font-bold text-white"
            style={{ background: '#F77F11', animation: 'kx-begin-glow 2.6s ease-in-out infinite' }}>
            <Sparkles className="w-5 h-5" />
            {isEs ? 'Comienza la experiencia KITXENS' : 'Begin the KITXENS Experience'}
          </button>
        </span>
        <p className="mt-5 text-[12px] text-white/40">{isEs ? 'Mejor con sonido — sube un poco el volumen.' : 'Best with sound — turn your volume up a little.'}</p>
        <p className="mt-2 text-[12.5px] text-white/60" data-testid="present-hands-free-hint">
          {dedication
            ? (isEs ? 'Toca el botón y déjate llevar — esto se preparó especialmente para ti.' : 'Tap the button and let it carry you — this was made especially for you.')
            : (isEs ? 'Siéntate y déjala correr — la experiencia se conduce sola. Un click y tomas el control cuando quieras.'
              : 'Sit back and let it run — the experience drives itself. One click and you take the wheel anytime.')}
        </p>
        {!dedication && guestName && (
          <p className="mt-3 text-[12.5px] text-white/55" data-testid="present-guest-greeting">
            {isEs ? `Todo listo para ti, ${guestName}.` : `All set for you, ${guestName}.`}
          </p>
        )}
        {!dedication && autoLeft > 0 && autoLeft <= 12 && (
          <p className="mt-3 text-[11.5px] text-[#F77F11]/90 font-semibold" data-testid="present-autostart-hint">
            {isEs ? `La experiencia comenzará sola en ${autoLeft}s…` : `The experience will start on its own in ${autoLeft}s…`}
          </p>
        )}
        {!dedication && demoSlug && (
          <div className="mt-7" data-testid="present-profile-picker">
            <p className="text-[10.5px] uppercase tracking-[0.3em] font-bold text-white/40">
              {isEs ? '¿Qué tipo de restaurante tienes?' : 'What kind of restaurant do you have?'}
            </p>
            <div className="mt-3 flex flex-wrap justify-center gap-2 max-w-md mx-auto">
              {PROFILE_CHOICES.slice(0, 8).map((p) => (
                <button key={p.key} data-testid={`present-profile-${p.key}`}
                  onClick={() => { if (demoSlug !== p.slug) switchDemoProfile(p.key, true); }}
                  className={`rounded-full px-3.5 py-1.5 text-[12px] font-bold tracking-wide transition-all duration-200 ${demoSlug === p.slug
                    ? 'bg-[#F77F11] text-white shadow-[0_0_18px_rgba(247,127,17,0.4)]'
                    : 'border border-white/25 text-white/70 hover:text-white hover:border-white/60'}`}>
                  {isEs ? p.es : p.en}
                </button>
              ))}
            </div>
            <p className="mt-2 text-[10.5px] text-white/35">
              {isEs ? 'Toda la demo — menú, ventas y reseñas — se adapta a tu cocina.' : 'The whole demo — menu, sales and reviews — adapts to your cuisine.'}
            </p>
          </div>
        )}
        {resumeInfo && (
          <button onClick={onResume} data-testid="present-resume-btn"
            className="mt-5 mx-auto block text-[13px] font-semibold text-white/70 hover:text-[#F77F11] underline decoration-dotted underline-offset-4 transition-colors">
            {isEs ? `▶ Te extrañamos — continúa donde lo dejaste (${resumeInfo.label})` : `▶ We missed you — continue where you left off (${resumeInfo.label})`}
          </button>
        )}
      </div>
      {guide && (
        <div className="fixed z-[97] pointer-events-none" data-testid="begin-gate-guide"
          style={{ left: 0, top: 0, transform: `translate(${pos.x}px, ${pos.y}px)`,
            transition: 'transform 1.4s cubic-bezier(0.3,0.9,0.35,1)' }}>
          <svg width="26" height="26" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))' }}>
            <path d="M5.5 3.2 19 11.6l-6.2 1.2-3.1 5.7z" fill="#fff" stroke="#1a1b1e" strokeWidth="1.4" strokeLinejoin="round" />
          </svg>
          <div className="absolute top-6 w-56 rounded-2xl px-4 py-3 border border-white/15 shadow-2xl"
            style={{ ...(pos.x + 270 > window.innerWidth ? { right: 6 } : { left: 24 }), background: 'rgba(18,19,22,0.95)', backdropFilter: 'blur(14px)', animation: 'kx-intro-line 0.8s 0.9s cubic-bezier(0.16,1,0.3,1) both' }}>
            <p className="text-[9.5px] uppercase tracking-[0.25em] font-bold text-[#F77F11] mb-1">Rachel</p>
            <p className="text-[13px] leading-relaxed text-white/95" style={{ fontFamily: 'Georgia, serif' }}>
              {isEs ? 'Haz clic aquí — aquí empieza todo.' : 'Click here — this is where it all begins.'}
            </p>
          </div>
        </div>
      )}
      <style>{`
        @keyframes kx-intro-line { 0% { opacity: 0; transform: translateY(26px) scale(0.97); filter: blur(8px); } 100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); } }
        @keyframes kx-begin-glow { 0%, 100% { box-shadow: 0 0 0 rgba(247,127,17,0.0); transform: scale(1); } 50% { box-shadow: 0 0 34px rgba(247,127,17,0.55); transform: scale(1.03); } }
        @keyframes kx-gate-halo { 0%, 100% { opacity: 0.9; transform: scale(1); } 50% { opacity: 0.35; transform: scale(1.12); } }
      `}</style>
    </div>
  );
}

// Spain & Latin America only: Spanish browser/site language, geo country, or a Spanish-world timezone.
const HISPANIC_MARKETS = ['ES', 'MX', 'AR', 'CO', 'PE', 'VE', 'CL', 'EC', 'GT', 'CU', 'BO', 'DO', 'HN', 'PY', 'SV', 'NI', 'CR', 'PA', 'UY', 'PR', 'GQ', 'BR'];
function isHispanicVisitor() {
  try {
    const langs = [navigator.language, ...(navigator.languages || [])].filter(Boolean).map((l) => String(l).toLowerCase());
    if (langs.some((l) => l.startsWith('es'))) return true;
    const market = (localStorage.getItem('kx_market') || '').toUpperCase();
    if (market && HISPANIC_MARKETS.includes(market)) return true;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz === 'Europe/Madrid' || tz === 'Atlantic/Canary' || tz === 'Africa/Ceuta') return true;
    if (tz.startsWith('America/')) {
      const anglo = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix',
        'America/Anchorage', 'America/Detroit', 'America/Boise', 'America/Juneau', 'America/Indiana', 'America/Kentucky',
        'America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg', 'America/Halifax', 'America/St_Johns',
        'America/Nassau', 'America/Jamaica', 'America/Barbados', 'America/Belize', 'America/Guyana', 'America/Port_of_Spain'];
      return !anglo.some((a) => tz.startsWith(a));
    }
  } catch { /* noop */ }
  return false;
}

const JLO_GREETING = '¡Hola! Gracias por estar. Soy Rachel... hoy con sabor latino. ¿Ready? Porque tu restaurante y yo... vamos a hacer something beautiful.';
const JLO_LINES = ['¡Hola! Gracias por estar.', 'Soy Rachel... hoy con sabor latino.', '¿Ready?', 'Tu restaurante y yo... vamos a hacer something beautiful.'];

function JenniferGreeting({ onDone }) {
  const [step, setStep] = useState(0);
  const v = VOICES.find((x) => x.id === 'nova');
  useEffect(() => {
    let alive = true;
    let finished = false;
    const finish = () => { if (alive && !finished) { finished = true; setTimeout(onDone, 700); } };
    const failsafe = setTimeout(finish, 12000); // never hold the show hostage
    speakLine(JLO_GREETING, 'es', 'nova').then(({ done }) => done.then(finish)).catch(finish);
    const iv = setInterval(() => setStep((s) => Math.min(s + 1, JLO_LINES.length - 1)), 2600);
    return () => { alive = false; clearTimeout(failsafe); clearInterval(iv); stopAllVoices(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div onClick={onDone} className="fixed inset-0 z-[95] flex items-center justify-center px-6 cursor-pointer" data-testid="present-jlo-saludo"
      style={{ background: 'radial-gradient(ellipse at center, #16171a 0%, #060607 78%)' }}>
      <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: 'radial-gradient(ellipse 60% 38% at 50% 58%, rgba(247,127,17,0.12), transparent 70%)' }} />
      <div className="max-w-2xl text-center" style={{ animation: 'kx-caption-in 0.8s cubic-bezier(0.16,1,0.3,1) both' }}>
        <img src={v?.img} alt="Jennifer L." className="w-24 h-24 rounded-full object-cover mx-auto border-2 border-[#F77F11]/70 shadow-[0_0_44px_rgba(247,127,17,0.4)]" />
        <p className="mt-5 text-[10px] uppercase tracking-[0.5em] font-bold text-[#F77F11]">RACHEL · CON SABOR LATINO</p>
        <p key={step} className="mt-6 text-3xl sm:text-4xl text-white leading-snug"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif', animation: 'kx-caption-in 1.1s cubic-bezier(0.16,1,0.3,1) both' }}
          data-testid="present-jlo-line">
          {JLO_LINES[step]}
        </p>
      </div>
      <span className="absolute bottom-8 right-8 text-[11px] uppercase tracking-[0.25em] text-white/35 font-bold">Toca para saltar</span>
      <style>{'@keyframes kx-caption-in { 0% { opacity: 0; transform: translateY(18px); filter: blur(6px); } 100% { opacity: 1; transform: translateY(0); filter: blur(0); } }'}</style>
    </div>
  );
}

const DEDICATION_TEXT = (name) => `${name}... esta experiencia es un regalo para ti. De alguien que te ama profundamente... y que sabe todo, todo lo que has hecho por él. Gracias por cada día... por creer siempre. Esto... es para ti.`;
const DEDICATION_LINES = (name) => [
  `${name}...`,
  'Esta experiencia es un regalo para ti.',
  'De alguien que te ama profundamente...',
  'y que sabe todo lo que has hecho por él.',
  'Gracias por cada día... por creer siempre.',
  'Esto... es para ti. ❤',
];

function DedicationScene({ name, message, voice, onDone }) {
  const [step, setStep] = useState(0);
  const spoken = message ? `${name}... ${message}` : DEDICATION_TEXT(name);
  const lines = message
    ? [`${name}...`, ...message.split(/(?<=[.!?…])\s+/).filter(Boolean), '❤']
    : DEDICATION_LINES(name);
  useEffect(() => {
    let alive = true;
    let finished = false;
    const finish = () => { if (alive && !finished) { finished = true; setTimeout(onDone, 900); } };
    const failsafe = setTimeout(finish, Math.max(26000, spoken.length * 80)); // never hold her hostage
    speakLine(spoken, 'es', voice || 'onyx').then(({ done }) => done.then(finish)).catch(finish);
    const iv = setInterval(() => setStep((s) => Math.min(s + 1, lines.length - 1)), 3400);
    return () => { alive = false; clearTimeout(failsafe); clearInterval(iv); stopAllVoices(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div onClick={onDone} className="fixed inset-0 z-[95] flex items-center justify-center px-6 cursor-pointer" data-testid="present-dedication"
      style={{ background: 'radial-gradient(ellipse at center, #16171a 0%, #060607 78%)' }}>
      <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: 'radial-gradient(ellipse 60% 38% at 50% 58%, rgba(247,127,17,0.14), transparent 70%)' }} />
      <div className="max-w-2xl text-center" style={{ animation: 'kx-caption-in 0.8s cubic-bezier(0.16,1,0.3,1) both' }}>
        <p className="text-[10px] uppercase tracking-[0.5em] font-bold text-[#F77F11]">{`PARA ${name.toUpperCase()} · CON TODO EL AMOR`}</p>
        <p key={step} className="mt-7 text-3xl sm:text-4xl text-white leading-snug"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif', animation: 'kx-caption-in 1.1s cubic-bezier(0.16,1,0.3,1) both' }}
          data-testid="present-dedication-line">
          {lines[step]}
        </p>
      </div>
      <span className="absolute bottom-8 right-8 text-[11px] uppercase tracking-[0.25em] text-white/35 font-bold">Toca para continuar</span>
      <style>{'@keyframes kx-caption-in { 0% { opacity: 0; transform: translateY(18px); filter: blur(6px); } 100% { opacity: 1; transform: translateY(0); filter: blur(0); } }'}</style>
    </div>
  );
}

function AudioUnlockPill({ isEs }) {
  return (
    <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[98] pointer-events-none flex items-center gap-2 rounded-full px-4 py-2 border border-white/20 shadow-2xl"
      style={{ background: 'rgba(18,19,22,0.92)', backdropFilter: 'blur(14px)', animation: 'kx-pill-glow 2.6s ease-in-out infinite' }}
      data-testid="present-audio-unlock-pill">
      <Volume2 className="w-4 h-4 text-[#F77F11]" />
      <span className="text-[12.5px] font-semibold text-white">
        {isEs ? 'Toca donde sea para activar la voz de Rachel' : "Tap anywhere to turn on Rachel's voice"}
      </span>
      <style>{'@keyframes kx-pill-glow { 0%, 100% { box-shadow: 0 0 0 rgba(247,127,17,0); } 50% { box-shadow: 0 0 26px rgba(247,127,17,0.5); } }'}</style>
    </div>
  );
}

function FloatingVolume({ isEs }) {
  const [vol, setVol] = useState(() => Math.round(getMasterVolume() * 100));
  const prevRef = React.useRef(60);
  const muted = vol === 0;
  const apply = (n) => { setVol(n); setMasterVolume(n / 100); if (n > 0) prevRef.current = n; };
  return (
    <div className="fixed bottom-24 right-4 sm:bottom-6 sm:right-6 z-[97] flex items-center gap-2 rounded-full px-3 py-2 border border-white/15 shadow-2xl"
      style={{ background: 'rgba(18,19,22,0.88)', backdropFilter: 'blur(16px)' }} data-testid="rest-present-volume-dock">
      <button onClick={() => apply(muted ? prevRef.current : 0)} data-testid="rest-present-mute"
        aria-label={muted ? 'Unmute' : 'Mute'}
        title={muted ? (isEs ? 'Activar sonido' : 'Unmute') : (isEs ? 'Silenciar todo' : 'Mute everything')}
        className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors hover:bg-white/10 ${muted ? 'text-white/40 hover:text-white' : 'text-[#F77F11]'}`}>
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
      <input type="range" min="0" max="100" value={vol} onChange={(e) => apply(Number(e.target.value))}
        data-testid="rest-present-volume" title={isEs ? 'Volumen (voz y música)' : 'Volume (voice & music)'}
        className="w-20 h-1 accent-[#F77F11] cursor-pointer" />
    </div>
  );
}

export function PresentationMode({ isEs, active, onExit, demoSlug, multiLoc }) {
  const nav = useNavigate();
  const [idx, setIdx] = useState(0);
  // Multi-location tenants get a dedicated Locations scene — the presentation mirrors their real panel.
  const VIEWSL = React.useMemo(() => (multiLoc
    ? [...VIEWS.slice(0, 3), ['locations', 'Locales', 'Locations'], ...VIEWS.slice(3)]
    : VIEWS), [multiLoc]);
  const [paused, setPaused] = useState(false);
  const [barMin, setBarMin] = useState(false); // one bar, and it gets out of the way when you want
  const [profOpen, setProfOpen] = useState(false); // switch restaurant mid-show
  const [mini, setMini] = useState(false); // YouTube-style: the show docks bottom-right while YOU drive
  const [mvol, setMvol] = useState(() => Math.round(getMasterVolume() * 100));
  const mprev = React.useRef(60);
  const applyVol = (n) => { setMvol(n); setMasterVolume(n / 100); if (n > 0) mprev.current = n; };
  const [tick, setTick] = useState(0);
  const [phase, setPhase] = useState('begin'); // begin → pick → intro → run
  const phaseRef = React.useRef('begin');
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  const guestName = React.useMemo(getGuestName, []);
  const [audioPending, setAudioPending] = useState(false); // auto-start: audio waits for the first touch
  const [coach, setCoach] = useState(false); // "let it run" whisper on first run entry
  const [pauseHint, setPauseHint] = useState(false); // first human pause: how to resume
  const [dedication] = useState(() => { try { return sessionStorage.getItem('kx_dedication_to') || ''; } catch { return ''; } });
  const [dedMsg] = useState(() => { try { return sessionStorage.getItem('kx_dedication_msg') || ''; } catch { return ''; } });
  const [dedVoice] = useState(() => { try { return sessionStorage.getItem('kx_dedication_voice') || 'onyx'; } catch { return 'onyx'; } });
  const [dedSign] = useState(() => { try { return sessionStorage.getItem('kx_dedication_sign') || ''; } catch { return ''; } });
  const [askResume, setAskResume] = useState(false); // paused and idle? Rachel asks "¿Seguimos?"
  const [calmNote, setCalmNote] = useState(false); // "take your time" — never pushy
  const [laterTick, setLaterTick] = useState(0); // "Sigo mirando" re-arms the ask timer
  const unlockTsRef = React.useRef(0);
  const coachSeenRef = React.useRef(false);
  const pauseHintSeenRef = React.useRef(false);
  const [voice, setVoice] = useState(() => { try { return localStorage.getItem('kx_rachel_voice') || 'shimmer'; } catch { return 'shimmer'; } });
  const [voiceOn, setVoiceOn] = useState(() => { try { return localStorage.getItem('kx_present_voice') !== '0'; } catch { return true; } });
  const toggleVoice = () => setVoiceOn((v) => { try { localStorage.setItem('kx_present_voice', v ? '0' : '1'); } catch { /* noop */ } return !v; });

  const goTo = useCallback((i) => {
    const n = ((i % VIEWSL.length) + VIEWSL.length) % VIEWSL.length;
    setIdx(n);
    setTick((t) => t + 1);
    // During the run the DIRECTOR navigates by really clicking the menu item —
    // perfect narration↔UI sync. Outside the run we route directly.
    if (phaseRef.current !== 'run') nav(VIEWSL[n][0] ? `/restaurant/${VIEWSL[n][0]}` : '/restaurant');
    window.scrollTo({ top: 0 });
  }, [nav]);

  useEffect(() => {
    if (!active) return undefined;
    document.documentElement.requestFullscreen?.().catch(() => {});
    setPhase('begin');
    goTo(0);
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (document.fullscreenElement) return; // the fullscreenchange handler decides
        if (phaseRef.current === 'run') setMini((m) => !m); // ESC docks/undocks the show, YouTube-style
        else { stopTheme(true); stopAllVoices(); onExit(); }
      }
      if (e.key === 'ArrowRight') goTo(idx + 1); if (e.key === 'ArrowLeft') goTo(idx - 1);
    };
    window.addEventListener('keydown', onKey);
    document.documentElement.classList.add('kx-present-active');
    // Browsers swallow the first ESC to leave fullscreen — treat that as exit too, never a stuck screen.
    const onFsChange = () => {
      if (document.fullscreenElement) return;
      if (phaseRef.current === 'run') setMini(true); // ESC out of fullscreen mid-show = minimize, never a dead end
      else { stopTheme(true); stopAllVoices(); onExit(); }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFsChange);
      document.documentElement.classList.remove('kx-present-active');
      stopTheme(true); // the score bows out gently — never abrupt
      stopAllVoices();
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const advance = useCallback(() => {
    if (idx >= VIEWSL.length - 1) setPhase('finale');
    else goTo(idx + 1);
  }, [idx, goTo]);

  // Remember where they are — so a returning guest continues, never restarts.
  useEffect(() => {
    try {
      if (phase === 'run') localStorage.setItem('kx_present_progress', JSON.stringify({ idx, t: Date.now() }));
      if (phase === 'finale') localStorage.removeItem('kx_present_progress');
    } catch { /* noop */ }
  }, [phase, idx]);

  // Switching kitchens mid-show: the tour continues at the SAME scene in the new restaurant —
  // no gate, no intro, audio politely waits for the first touch (unlock pill).
  useEffect(() => {
    if (!active || phase !== 'begin') return;
    let sw = null;
    try { sw = sessionStorage.getItem('kx_present_switch'); if (sw) sessionStorage.removeItem('kx_present_switch'); } catch { /* noop */ }
    if (!sw) return;
    let saved = null;
    try {
      const p = JSON.parse(localStorage.getItem('kx_present_progress') || 'null');
      if (p && p.idx >= 0 && p.idx < VIEWSL.length) saved = p;
    } catch { /* noop */ }
    startTheme();
    setAudioPending(true);
    setPhase('run');
    goTo(saved ? saved.idx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, phase]);

  // Out of sight = out of sound. Switching tabs/windows silences EVERYTHING instantly.
  // Coming back is seamless: the show simply continues where it was — no questions asked.
  const [showBack, setShowBack] = useState(false);
  const awayRef = React.useRef(false);
  useEffect(() => {
    if (!active) return undefined;
    const onVis = () => {
      if (document.hidden) {
        stopAllVoices();
        pauseThemeSoft();
        if (phase === 'run' && !paused) { awayRef.current = true; setPaused(true); }
      } else if (awayRef.current) {
        awayRef.current = false;
        resumeThemeSoft();
        setPaused(false); // the same scene picks up right where it left off
        setShowBack(true); // a brief, warm hello — never a blocker, never an interrogation
      } else {
        resumeThemeSoft(); // begin/pick/intro phases: the score simply returns
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [active, phase, paused]);
  useEffect(() => {
    if (!showBack) return undefined;
    const t = setTimeout(() => setShowBack(false), 3200);
    return () => clearTimeout(t);
  }, [showBack]);

  // Auto-start left audio locked? The very first touch turns everything on — and never pauses the show.
  useEffect(() => {
    if (!active || !audioPending) return undefined;
    const onGesture = (e) => {
      if (!e.isTrusted) return;
      unlockTsRef.current = Date.now();
      setAudioPending(false);
      startTheme(); // recreates the score if autoplay had blocked it
      resumeThemeSoft();
    };
    window.addEventListener('pointerdown', onGesture, true);
    window.addEventListener('keydown', onGesture, true);
    return () => { window.removeEventListener('pointerdown', onGesture, true); window.removeEventListener('keydown', onGesture, true); };
  }, [active, audioPending]);

  // First arrival in run: a gentle coach so no one wrestles the wheel by accident.
  useEffect(() => {
    if (phase === 'run' && !coachSeenRef.current) {
      coachSeenRef.current = true;
      setCoach(true);
      const t = setTimeout(() => setCoach(false), 10000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [phase]);

  // Language is sacred: EN or ES, never blended. Switching cuts any playing voice instantly
  // and the show resumes from this exact moment in the new language.
  const firstLangRef = React.useRef(true);
  useEffect(() => {
    if (firstLangRef.current) { firstLangRef.current = false; return; }
    stopAllVoices();
  }, [isEs]);

  // Paused and gone quiet for 12s? Rachel gently asks: "¿Seguimos?" — human, never pushy.
  useEffect(() => {
    if (!active || phase !== 'run' || !paused) { setAskResume(false); return undefined; }
    let idle;
    const arm = () => { clearTimeout(idle); idle = setTimeout(() => setAskResume(true), 12000); };
    arm();
    const onActivity = (e) => {
      if (!e.isTrusted) return;
      // Clicks inside the bubble are answers, not "activity" — let its buttons do their job.
      if (e.target && e.target.closest && e.target.closest('[data-testid="present-ask-resume"]')) return;
      setAskResume(false); arm();
    };
    window.addEventListener('click', onActivity, true);
    window.addEventListener('wheel', onActivity, true);
    window.addEventListener('keydown', onActivity);
    return () => {
      clearTimeout(idle);
      window.removeEventListener('click', onActivity, true);
      window.removeEventListener('wheel', onActivity, true);
      window.removeEventListener('keydown', onActivity);
    };
  }, [active, phase, paused, laterTick]);

  // The show has begun the moment they choose to begin — the score never waits, never stops.
  useEffect(() => {
    if (!active || phase === 'begin') return undefined;
    const iv = setInterval(ensureTheme, 4000);
    return () => clearInterval(iv);
  }, [active, phase]);

  // A dedicated link warms its love letter early — Alejandro speaks the moment she begins.
  useEffect(() => {
    if (dedication && phase === 'begin') {
      prewarmLine(dedMsg ? `${dedication}... ${dedMsg}` : DEDICATION_TEXT(dedication), 'es', dedVoice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dedication, phase]);

  // Jennifer's Spanglish hello warms up while they browse voices — instant when picked.
  useEffect(() => {
    if (phase === 'pick' && (isEs || isHispanicVisitor())) prewarmLine(JLO_GREETING, 'es', 'nova');
  }, [phase, isEs]);

  useEffect(() => {
    if (!active || paused || mini || phase !== 'run') return undefined;
    const t = setTimeout(advance, INTERVAL);
    return () => clearTimeout(t);
  }, [active, paused, mini, idx, tick, advance, phase]);

  useEffect(() => {
    if (!active) return undefined;
    const el = document.documentElement;
    const lock = phase === 'begin' || phase === 'pick' || phase === 'dedica' || phase === 'saludo' || phase === 'intro' || phase === 'finale';
    el.style.overflow = lock ? 'hidden' : '';
    el.classList.remove('kx-present-cursorless'); // the guest's real cursor is ALWAYS visible
    return () => { el.style.overflow = ''; };
  }, [active, phase, paused]);

  // Co-pilot mode: a real click hands the wheel to the guest WITHOUT stopping the
  // show — the ghost cursor steps aside and Rachel keeps presenting. ⏸ still pauses.
  useEffect(() => {
    if (!active || phase !== 'run' || paused || mini) return undefined; // docked: the presenter drives WHILE the show keeps rolling
    const onDocClick = (e) => {
      if (!e.isTrusted) return; // Rachel's own scripted clicks are not the guest
      if (Date.now() - unlockTsRef.current < 900) return; // the audio-unlock tap is not "taking the wheel"
      // OS controls (language, theme, text size) are not "taking the wheel" either.
      if (e.target.closest('header') || e.target.closest('[data-testid="rest-present-bar"]') || e.target.closest('[data-testid="rest-present-volume-dock"]')) return;
      window.dispatchEvent(new Event('kx-user-control'));
      if (!pauseHintSeenRef.current) {
        pauseHintSeenRef.current = true;
        setPauseHint(true);
        setTimeout(() => setPauseHint(false), 7000);
      }
    };
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  }, [active, phase, paused, mini]);

  // Never stuck: if the guest pauses and then walks away, the show politely
  // resumes on its own after 45s of inactivity. The demo always keeps moving.
  useEffect(() => {
    if (!active || phase !== 'run' || !paused) return undefined;
    let idle;
    const arm = () => { clearTimeout(idle); idle = setTimeout(() => setPaused(false), 45000); };
    arm();
    // Moving the mouse alone never blocks the show — only real engagement (click/scroll/type) keeps the pause alive.
    const onActivity = (e) => { if (e.isTrusted) arm(); };
    window.addEventListener('click', onActivity, true);
    window.addEventListener('wheel', onActivity, true);
    window.addEventListener('keydown', onActivity);
    return () => {
      clearTimeout(idle);
      window.removeEventListener('click', onActivity, true);
      window.removeEventListener('wheel', onActivity, true);
      window.removeEventListener('keydown', onActivity);
    };
  }, [active, phase, paused]);

  // Resuming (Play or watchdog) returns the show to its exact scene — even if the
  // guest wandered to another view while paused. Rachel continues where she left off.
  const wasPausedRef = React.useRef(false);
  useEffect(() => {
    if (phase !== 'run') return;
    if (wasPausedRef.current && !paused) {
      resumeThemeSoft(); // the score returns with the show
      nav(VIEWSL[idx][0] ? `/restaurant/${VIEWSL[idx][0]}` : '/restaurant');
    }
    wasPausedRef.current = paused;
  }, [paused, phase, idx, nav]);

  // Docked (YouTube-style): the presenter drives and talks; Rachel waits silently.
  // Restoring returns to the exact scene and re-narrates it — never restarts the show.
  const wasMiniRef = React.useRef(false);
  useEffect(() => {
    if (phase !== 'run') return;
    if (mini) { stopAllVoices(); pauseThemeSoft(); }
    else if (wasMiniRef.current) {
      resumeThemeSoft();
      nav(VIEWSL[idx][0] ? `/restaurant/${VIEWSL[idx][0]}` : '/restaurant');
      setTick((t) => t + 1);
    }
    wasMiniRef.current = mini;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mini, phase]);

  if (!active) return null;
  if (phase === 'begin') {
    const saved = (() => {
      try {
        const p = JSON.parse(localStorage.getItem('kx_present_progress') || 'null');
        if (p && p.idx > 0 && p.idx < VIEWSL.length && Date.now() - p.t < 7 * 864e5) return p;
      } catch { /* noop */ }
      return null;
    })();
    return (<>
      <BeginGate isEs={isEs} dedication={dedication} dedicationSign={dedSign} demoSlug={demoSlug} guestName={guestName}
        onBegin={() => {
          startTheme();
          if (dedication) {
            // Her show, his words: the chosen voice carries the dedication, then the experience.
            setVoice(dedVoice); setVoiceOn(true);
            try { localStorage.setItem('kx_rachel_voice', dedVoice); localStorage.setItem('kx_present_voice', '1'); } catch { /* noop */ }
            setPhase('dedica');
          } else setPhase('pick');
        }}
        onAutoBegin={() => {
          // Auto mode: a voice ALWAYS joins — their saved Rachel, or a random one. Never a silent show.
          let v = null;
          if (dedication) v = dedVoice; // the dedication arrives in its chosen voice
          else {
            try { v = localStorage.getItem('kx_rachel_voice'); } catch { /* noop */ }
            if (!v) v = VOICES[Math.floor(Math.random() * VOICES.length)].id;
          }
          try { localStorage.setItem('kx_rachel_voice', v); } catch { /* noop */ }
          setVoice(v);
          setVoiceOn(true);
          try { localStorage.setItem('kx_present_voice', '1'); } catch { /* noop */ }
          startTheme();
          setAudioPending(true); // browsers hold audio until a touch — the pill invites it
          setPhase(dedication ? 'dedica' : 'intro');
        }}
        resumeInfo={saved ? { idx: saved.idx, label: isEs ? VIEWSL[saved.idx][1] : VIEWSL[saved.idx][2] } : null}
        onResume={saved ? () => { startTheme(); setPhase('run'); goTo(saved.idx); } : null} />
      <FloatingVolume isEs={isEs} />
    </>);
  }
  if (phase === 'pick') {
    return (<>
      <VoicePicker isEs={isEs} current={voice}
        onPick={(v) => {
          if (v) { setVoice(v); setVoiceOn(true); try { localStorage.setItem('kx_rachel_voice', v); localStorage.setItem('kx_present_voice', '1'); } catch { /* noop */ } }
          else { setVoiceOn(false); try { localStorage.setItem('kx_present_voice', '0'); } catch { /* noop */ } }
          // Jennifer greets Spain & Latin America in warm Spanglish — the Latin touch, felt instantly.
          if (v === 'nova' && (isEs || isHispanicVisitor())) setPhase('saludo');
          else setPhase('intro');
        }} />
      <FloatingVolume isEs={isEs} />
    </>);
  }
  if (phase === 'dedica') {
    return (<>
      <DedicationScene name={dedication || 'Para ti'} message={dedMsg} voice={dedVoice} onDone={() => setPhase('intro')} />
      {audioPending && <AudioUnlockPill isEs={isEs} />}
      <FloatingVolume isEs={isEs} />
    </>);
  }
  if (phase === 'saludo') {
    return (<>
      <JenniferGreeting onDone={() => setPhase('intro')} />
      <FloatingVolume isEs={isEs} />
    </>);
  }
  if (phase === 'intro') {
    return (<>
      <CinematicIntro isEs={isEs} voiceOn={voiceOn} voice={voice} onDone={() => setPhase('run')} />
      {audioPending && <AudioUnlockPill isEs={isEs} />}
      <FloatingVolume isEs={isEs} />
    </>);
  }
  if (phase === 'finale') {
    return (<>
      <FinaleScreen isEs={isEs} voiceOn={voiceOn} voice={voice}
        onExplore={onExit}
        onFallback={() => { onExit(); nav('/contact'); }} />
      <FloatingVolume isEs={isEs} />
    </>);
  }
  return (
    <>
    <PresentDirector active={active && !paused && !mini} isEs={isEs} viewKey={VIEWSL[idx][0]} runId={tick} voiceOn={voiceOn} voice={voice}
      navTo={nav} onDone={advance} demoSlug={demoSlug || ''} />
    {audioPending && <AudioUnlockPill isEs={isEs} />}
    {mini && (
      <div className="fixed bottom-4 right-4 z-[90] w-64 max-w-[86vw] rounded-2xl border border-white/15 shadow-2xl p-3.5"
        style={{ background: 'rgba(18,19,22,0.94)', backdropFilter: 'blur(16px)', animation: 'kx-coach-in 0.4s ease both' }}
        data-testid="rest-present-mini">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#F77F11] animate-pulse shrink-0" />
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#F77F11]">{isEs ? 'Demo en espera' : 'Show docked'}</p>
        </div>
        <p className="mt-1.5 text-[12.5px] font-semibold text-white truncate">{isEs ? VIEWSL[idx][1] : VIEWSL[idx][2]} · {idx + 1}/{VIEWSL.length}</p>
        <p className="mt-0.5 text-[10.5px] text-white/50">{isEs ? 'Tú tienes el control. Esc o ▶ para continuar.' : 'You drive. Esc or ▶ to continue.'}</p>
        <div className="mt-2.5 flex items-center gap-1.5">
          <button onClick={() => setMini(false)} data-testid="rest-present-mini-resume"
            className="flex-1 h-8 rounded-full bg-[#F77F11] flex items-center justify-center gap-1.5 text-white text-[11.5px] font-bold shadow-lg shadow-orange-500/30 hover:brightness-110 transition-all">
            <Play className="w-3.5 h-3.5" /> {isEs ? 'Continuar' : 'Resume'}
          </button>
          <button onClick={onExit} data-testid="rest-present-mini-exit" aria-label="Exit presentation"
            title={isEs ? 'Salir de la presentación' : 'Exit presentation'}
            className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    )}
    <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] w-max max-w-[96vw] ${mini ? 'hidden' : ''}`} data-testid="rest-present-bar">
      {coach && !paused && (
        <div className="absolute bottom-full left-1/2 mb-3 w-80 max-w-[90vw] rounded-2xl px-4 py-3 border border-white/15 shadow-2xl text-center pointer-events-none"
          style={{ background: 'rgba(18,19,22,0.95)', backdropFilter: 'blur(16px)', animation: 'kx-coach-in 0.5s ease both' }}
          data-testid="present-run-coach">
          <p className="text-[9.5px] uppercase tracking-[0.25em] font-bold text-[#F77F11] mb-1">Rachel</p>
          <p className="text-[12.5px] leading-relaxed text-white/95">
            {isEs ? 'Yo conduzco el tour — y puedes tocar lo que quieras sin detenerlo. Tu cursor manda; ⏸ pausa cuando quieras.'
              : 'I drive the tour — and you can click anything without stopping it. Your cursor leads; ⏸ pauses anytime.'}
          </p>
        </div>
      )}
      {askResume && paused && !showBack && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-72 max-w-[90vw] rounded-2xl px-4 py-3.5 border border-white/15 shadow-2xl text-center"
          style={{ background: 'rgba(18,19,22,0.95)', backdropFilter: 'blur(16px)', animation: 'kx-caption-in 0.5s ease both' }}
          data-testid="present-ask-resume">
          <p className="text-[9.5px] uppercase tracking-[0.25em] font-bold text-[#F77F11] mb-1">Rachel</p>
          <p className="text-[13.5px] font-semibold text-white">{isEs ? `¿Seguimos donde lo dejamos${guestName ? `, ${guestName}` : ''}?` : `Shall we continue where we left off${guestName ? `, ${guestName}` : ''}?`}</p>
          <div className="mt-2.5 flex items-center justify-center gap-2">
            <button onClick={() => { setAskResume(false); setPaused(false); }} data-testid="present-ask-resume-yes"
              className="rounded-full bg-[#F77F11] text-white text-[12px] font-bold px-4 py-1.5 hover:brightness-110 transition-all">
              {isEs ? '▶ Sí, seguimos' : "▶ Yes, let's go"}
            </button>
            <button onClick={() => { setAskResume(false); setLaterTick((t) => t + 1); setCalmNote(true); setTimeout(() => setCalmNote(false), 5000); }} data-testid="present-ask-resume-later"
              className="rounded-full border border-white/20 text-white/60 hover:text-white hover:border-white/40 text-[12px] px-3.5 py-1.5 transition-colors">
              {isEs ? 'Sigo mirando' : 'Still looking'}
            </button>
          </div>
          <p className="mt-2.5 text-[10.5px] text-white/45">{isEs ? 'O te enseño directamente:' : 'Or I can jump straight to:'}</p>
          <div className="mt-1.5 flex flex-wrap items-center justify-center gap-1.5">
            {[[2, 'Revenue'], [3, isEs ? 'Menú' : 'Menu'], [5, 'AI Executive']].map(([i, label]) => (
              <button key={label} onClick={() => { setAskResume(false); setPaused(false); goTo(i); }}
                data-testid={`present-ask-jump-${i}`}
                className="rounded-full border border-[#F77F11]/50 text-[#F77F11] text-[11px] font-semibold px-3 py-1 hover:bg-[#F77F11]/10 transition-colors">
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
      {calmNote && paused && !askResume && !showBack && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 rounded-full px-5 py-2.5 border border-white/15 shadow-2xl whitespace-nowrap pointer-events-none"
          style={{ background: 'rgba(18,19,22,0.95)', backdropFilter: 'blur(16px)', animation: 'kx-coach-in 0.5s ease both' }}
          data-testid="present-calm-note">
          <p className="text-[12.5px] font-semibold text-white">
            {isEs ? `Tómate tu tiempo${guestName ? `, ${guestName}` : ''}. Aquí estaré. 🧡` : `Take your time${guestName ? `, ${guestName}` : ''}. I’ll be right here. 🧡`}
          </p>
        </div>
      )}
      {pauseHint && !showBack && !askResume && (
        <div className="absolute bottom-full left-1/2 mb-3 w-72 max-w-[90vw] rounded-2xl px-4 py-3 border border-white/15 shadow-2xl text-center pointer-events-none"
          style={{ background: 'rgba(18,19,22,0.95)', backdropFilter: 'blur(16px)', animation: 'kx-coach-in 0.5s ease both' }}
          data-testid="present-pause-hint">
          <p className="text-[12.5px] leading-relaxed text-white/95">
            {isEs ? 'Tienes el control — la demo sigue contigo. Usa ⏸ cuando quieras pausarla.'
              : 'You have the wheel — the show keeps rolling with you. Use ⏸ anytime to pause.'}
          </p>
        </div>
      )}
      {showBack && (
        <div className="absolute bottom-full left-1/2 mb-3 -translate-x-1/2 rounded-full px-5 py-2.5 border border-white/15 shadow-2xl whitespace-nowrap"
          style={{ background: 'rgba(18,19,22,0.95)', backdropFilter: 'blur(16px)', animation: 'kx-coach-in 0.5s ease both' }}
          data-testid="present-welcome-back">
          <p className="text-[12.5px] font-semibold text-white">
            {isEs ? `¡Volviste${guestName ? `, ${guestName}` : ''}! Seguimos donde estabas 🧡` : `You’re back${guestName ? `, ${guestName}` : ''}! Picking up right where you were 🧡`}
          </p>
        </div>
      )}
      {profOpen && !barMin && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 rounded-2xl p-3 border border-white/15 shadow-2xl w-[19rem] max-w-[92vw]"
          style={{ background: 'rgba(18,19,22,0.96)', backdropFilter: 'blur(16px)', animation: 'kx-coach-in 0.35s ease both' }}
          data-testid="rest-present-profile-menu">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#F77F11] px-1">{isEs ? 'Cambiar de restaurante' : 'Switch restaurant'}</p>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {PROFILE_CHOICES.slice(0, 8).map((p) => (
              <button key={p.key} onClick={() => { if (demoSlug !== p.slug) switchDemoProfile(p.key, 'resume'); else setProfOpen(false); }}
                data-testid={`rest-present-profile-${p.key}`} title={isEs ? p.es : p.en}
                className={`rounded-lg px-2.5 py-2 text-[11.5px] font-bold text-left transition-all ${demoSlug === p.slug ? 'bg-[#F77F11]/20 ring-1 ring-[#F77F11] text-[#F77F11]' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}>
                {isEs ? p.es : p.en}
              </button>
            ))}
          </div>
          <p className="mt-2 px-1 text-[9.5px] text-white/40">{isEs ? 'Rachel reinicia su tour en la cocina que elijas.' : 'Rachel restarts her tour in the kitchen you pick.'}</p>
        </div>
      )}
      {barMin ? (
        <div className="flex items-center gap-1 rounded-full px-2 py-1.5 shadow-2xl border border-white/15"
          style={{ background: 'rgba(18,19,22,0.88)', backdropFilter: 'blur(16px)' }}>
          <button onClick={() => setPaused((p) => !p)} data-testid="rest-present-play"
            aria-label={paused ? 'Play' : 'Pause'}
            className="w-8 h-8 rounded-full bg-[#F77F11] flex items-center justify-center text-white shadow-lg shadow-orange-500/30 hover:brightness-110 transition-all">
            {paused ? <Play className="w-3.5 h-3.5 ml-0.5" /> : <Pause className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => setBarMin(false)} data-testid="rest-present-expand" aria-label="Expand controls"
            className="w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors">
            <ChevronUp className="w-4 h-4" />
          </button>
        </div>
      ) : (
      <>
      <div className="flex items-center gap-1.5 sm:gap-2 rounded-full px-3 sm:px-4 py-2 sm:py-2.5 shadow-2xl border border-white/15"
        style={{ background: 'rgba(18,19,22,0.88)', backdropFilter: 'blur(16px)' }}>
        <span className={`hidden sm:inline text-[10px] uppercase tracking-[0.2em] font-bold pr-1 ${paused ? 'text-white/70' : 'text-[#F77F11]'}`}>
          {paused ? (isEs ? 'Pausado · tú controlas' : 'Paused · you drive') : (isEs ? 'Presentación' : 'Presenting')}
        </span>
        <button onClick={() => goTo(idx - 1)} data-testid="rest-present-prev" aria-label="Previous"
          className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button onClick={() => setPaused((p) => !p)} data-testid="rest-present-play"
          aria-label={paused ? 'Play' : 'Pause'}
          className="w-9 h-9 rounded-full bg-[#F77F11] flex items-center justify-center text-white shadow-lg shadow-orange-500/30 hover:brightness-110 transition-all">
          {paused ? <Play className="w-4 h-4 ml-0.5" /> : <Pause className="w-4 h-4" />}
        </button>
        <button onClick={advance} data-testid="rest-present-next" aria-label="Next"
          className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors">
          <ChevronRight className="w-4 h-4" />
        </button>
        <span className="hidden sm:flex items-center gap-1.5 px-1.5">
          {VIEWSL.map(([v], i) => (
            <button key={v} onClick={() => goTo(i)} aria-label={VIEWSL[i][1]} data-testid={`rest-present-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-[#F77F11]' : 'w-1.5 bg-white/30 hover:bg-white/60'}`} />
          ))}
        </span>
        <span className="sm:hidden text-[10px] font-bold text-white/60 px-1">{idx + 1}/{VIEWSL.length}</span>
        <span className="hidden md:inline text-[11.5px] font-bold text-white/85 min-w-[130px]">{isEs ? VIEWSL[idx][1] : VIEWSL[idx][2]}</span>
        {demoSlug && (
          <button onClick={() => setProfOpen((o) => !o)} data-testid="rest-present-profiles" aria-label={isEs ? 'Cambiar de restaurante' : 'Switch restaurant'}
            title={isEs ? 'Cambiar de restaurante (la demo sigue contigo)' : 'Switch restaurant (the demo comes with you)'}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${profOpen ? 'text-[#F77F11] bg-white/10' : 'text-white/60 hover:text-white hover:bg-white/10'}`}>
            <UtensilsCrossed className="w-4 h-4" />
          </button>
        )}
        <button onClick={toggleVoice} data-testid="rest-present-voice" aria-label={voiceOn ? 'Mute voice' : 'Unmute voice'}
          title={voiceOn ? (isEs ? 'Silenciar la voz de Rachel' : "Mute Rachel's voice") : (isEs ? 'Activar la voz de Rachel' : "Turn on Rachel's voice")}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${voiceOn ? 'text-[#F77F11] hover:bg-white/10' : 'text-white/40 hover:text-white hover:bg-white/10'}`}>
          {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
        {voiceOn && (
          <button data-testid="rest-present-voice-style"
            onClick={() => {
              const i = VOICES.findIndex((v) => v.id === voice);
              const nv = VOICES[(i + 1) % VOICES.length].id;
              setVoice(nv);
              try { localStorage.setItem('kx_rachel_voice', nv); } catch { /* noop */ }
            }}
            title={isEs ? 'Cambiar la voz de Rachel (aplica al instante)' : "Change Rachel's voice (applies instantly)"}
            className="text-[10px] font-bold uppercase tracking-wide text-white/60 hover:text-white transition-colors px-1">
            {(VOICES.find((v) => v.id === voice) || VOICES[0]).label}
          </button>
        )}
        <span className="hidden sm:inline-flex" data-testid="rest-present-lang"><LangSwitch dark /></span>
        <span className="w-px h-5 bg-white/15 hidden sm:inline" />
        <button onClick={() => applyVol(mvol === 0 ? mprev.current : 0)} data-testid="rest-present-master-mute"
          aria-label={mvol === 0 ? 'Unmute all' : 'Mute all'}
          title={isEs ? 'Volumen general (música y voz)' : 'Master volume (music & voice)'}
          className="w-8 h-8 rounded-full hidden sm:flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors">
          {mvol === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <input type="range" min="0" max="100" value={mvol} onChange={(e) => applyVol(Number(e.target.value))}
          data-testid="rest-present-master-volume" aria-label="Master volume"
          className="hidden sm:inline-block w-20 accent-[#F77F11] cursor-pointer" />
        <button onClick={() => setMini(true)} data-testid="rest-present-minimize" aria-label="Minimize presentation"
          title={isEs ? 'Minimizar — tú navegas y la demo espera (Esc)' : 'Minimize — you drive while the show waits (Esc)'}
          className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors">
          <Minimize2 className="w-4 h-4" />
        </button>
        <button onClick={() => setBarMin(true)} data-testid="rest-present-collapse" aria-label="Minimize controls"
          title={isEs ? 'Minimizar controles' : 'Minimize controls'}
          className="w-8 h-8 rounded-full hidden sm:flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors">
          <ChevronDown className="w-4 h-4" />
        </button>
        <button onClick={onExit} data-testid="rest-present-exit" aria-label="Exit"
          className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
      {!paused && (
        <div key={`${idx}-${tick}`} className="mt-1.5 mx-4 h-0.5 rounded-full bg-white/15 overflow-hidden">
          <div className="h-full bg-[#F77F11] rounded-full" style={{ animation: `kx-present-progress ${INTERVAL}ms linear forwards` }} />
        </div>
      )}
      </>
      )}
      <style>{'@keyframes kx-present-progress { from { width: 0 } to { width: 100% } } @keyframes kx-coach-in { 0% { opacity: 0; transform: translate(-50%, 10px); } 100% { opacity: 1; transform: translate(-50%, 0); } }'}</style>
    </div>
    </>
  );
}
